/**
 * 断卦证据的白话层。
 *
 * 它只对 verdict().keyPoints 做替换和串接，不计分、不裁决、
 * 不添加原 keyPoint 里没有的原因、趋势或建议。
 */

import { verdict } from './verdict.js';
import { BRANCH_ELEMENT } from './ganzhi.js';
import {
  CATEGORY_FOCUS_LABELS, PLAIN_CHANGSHENG_STAGES, PLAIN_TERMS
} from './plain-terms.js';

export const PLAIN_VERDICT_NOTE = '以上是按传统规则得出的卦象依据，只表示大致倾向，不代表结果一定如此。';

const STATE_PLAIN = Object.freeze({
  旺: PLAIN_TERMS.旺,
  相: PLAIN_TERMS.相,
  休: PLAIN_TERMS.休,
  囚: PLAIN_TERMS.囚,
  死: PLAIN_TERMS.死
});

const RELATIVES = '父母|兄弟|子孙|妻财|官鬼';
const STEMS = '甲乙丙丁戊己庚辛壬癸';
const BRANCHES = '子丑寅卯辰巳午未申酉戌亥';
const ELEMENTS = '木火土金水';

const REPLACEMENTS = Object.freeze([
  ['用神不上卦', '所问之事的主角没有直接出现在卦面上'],
  ['伏神用神', '藏在卦面下的主角'],
  ['伏神1爻', '藏在卦面下的1爻'],
  ['对用神为', '对主角而言是'],
  ['为本卦主用神', '是这一卦中所问之事的主角'],
  ['克用神之', '制约主角的'],
  ['克用神', '妨碍所问之事的主角'],
  ['生用神', '帮助所问之事的主角'],
  ['用神无气', '主角当下力量不足'],
  ['无生助之源', '盘面中没有直接帮助它的五行来源'],
  ['飞神克伏神', PLAIN_TERMS.飞神克伏神],
  ['飞神生伏神', PLAIN_TERMS.飞神生伏神],
  ['得月建生扶', '得到当月节气之气的帮助'],
  ['得日辰生扶', '得到当天干支之气的帮助'],
  ['日辰克制伏神', '当天的干支之气制约藏起来的主角'],
  ['静而逢日辰', '原本不变但遇到当天的干支之气'],
  ['静爻之间作合', '两个原本不变的爻相合'],
  ['两爻皆静', '两爻都没有变化'],
  ['有一爻动', '其中一爻正在变化'],
  ['动而逢合', '正在变化时又遇到相合'],
  ['发动后变', '发生变化后变为'],
  ['发动变', '发生变化并变为'],
  ['发动克', '发生变化并制约'],
  ['发动生', '发生变化并帮助'],
  ['暗动克', '表面不动但实际在起作用，并制约'],
  ['暗动生', '表面不动但实际在起作用，并帮助'],
  ['忌神发动', '妨碍主角的力量正在变化'],
  ['原神发动', '帮助主角的力量正在变化'],
  ['五行克', '在五行关系上制约'],
  ['五行生', '在五行关系上帮助'],
  ['日月作用事实', '当月与当天的气对各爻的作用'],
  ['用神断点', '主角观察点'],
  ['卦象事实', '整体排列事实'],
  ['本卦', '当前卦'],
  ['全卦六爻安静', '六爻都没有变化'],
  ['所在卦六爻安静', '所在的卦里六爻都没有变化'],
  ['旺相不空不破', '力量充足，且没有暂时不在位或被冲散'],
  ['旺相而静', '力量充足且本身没有变化'],
  ['囚而待生扶', '处于受制状态，规则列出可提供力量的日期'],
  ['待长生支值日', '规则列出力量开始生发的地支值日'],
  ['值日出空', '遇到自身地支值日，不再处于暂时不在位'],
  ['冲变爻之空', '用相冲的地支解开变爻暂时不在位的状态'],
  ['地支六合', '两个地支相合'],
  ['世应相合', PLAIN_TERMS.世应相合]
]);

function replaceStateContexts(text) {
  let output = text;
  for (const [state, plain] of Object.entries(STATE_PLAIN)) {
    output = output
      .replaceAll(`旺衰为${state}`, `力量状态为${plain}`)
      .replaceAll(`为${state}；`, `处于${plain}状态；`)
      .replaceAll(`${state}而待`, `${plain}状态，规则等待`)
      .replaceAll(`（${state}、`, `（${plain}、`)
      .replaceAll(`（${state}）`, `（${plain}）`)
      .replace(new RegExp(`([木火土金水])${state}(?=[，；。])`, 'gu'), `$1${plain}`);
  }
  return output;
}

function replaceStageContexts(text) {
  let output = text;
  for (const [stage, plain] of Object.entries(PLAIN_CHANGSHENG_STAGES)) {
    output = output.replaceAll(`十二长生为${stage}`, plain);
  }
  return output;
}

function replaceCalendarContexts(text) {
  return text
    .replace(new RegExp(`([${BRANCHES}])月([${ELEMENTS}])([旺相休囚死])`, 'gu'),
      (_, branch, element, state) => {
        const monthElement = BRANCH_ELEMENT[branch];
        const effect = {
          旺: `${element}当令有力`,
          相: `${element}得${monthElement}帮助`,
          休: `${element}失时`,
          囚: `${element}受${monthElement}克制`,
          死: `${element}无力`
        }[state];
        return `当月是${branch}月（${monthElement}当令），${effect}`;
      })
    .replace(new RegExp(`([${STEMS}][${BRANCHES}])日([${ELEMENTS}])墓于([${BRANCHES}])`, 'gu'),
      '当天是$1日，$2遇到$3时力量被暂时收住')
    .replace(new RegExp(`([${STEMS}][${BRANCHES}])日([${ELEMENTS}])绝于([${BRANCHES}])`, 'gu'),
      '当天是$1日，$2遇到$3时力量衰到最低')
    .replace(new RegExp(`([${STEMS}][${BRANCHES}])日([${ELEMENTS}])克([${ELEMENTS}])`, 'gu'),
      '当天是$1日，$2制约$3')
    .replace(new RegExp(`([${STEMS}][${BRANCHES}])日([${ELEMENTS}])生([${ELEMENTS}])`, 'gu'),
      '当天是$1日，$2帮助$3');
}

function shortenRepeatedLineRefs(text, primary) {
  let output = text;
  const primaryFull = primary
    ? `${primary.position}爻${primary.relative}${primary.ganzhi}`
    : null;
  if (primaryFull) output = output.replaceAll(primaryFull, '代表爻');

  const seen = new Set();
  output = output.replace(
    new RegExp(`([1-6])爻(${RELATIVES})([${STEMS}][${BRANCHES}])`, 'gu'),
    (match, position, relative, ganzhi) => {
      const key = `${position}:${relative}:${ganzhi}`;
      if (!seen.has(key)) {
        seen.add(key);
        return match;
      }
      return `${relative}${ganzhi}`;
    }
  );
  return output
    .replaceAll('代表爻代表爻', '代表爻')
    .replaceAll('代表爻：是这一卦中代表爻；', '代表爻：')
    .replaceAll('以代表爻为代表爻观察点', '以代表爻为观察点');
}

/**
 * 同一条 keyPoint 可能同时携带「日墓」「入墓」和 marks 文本，
 * 翻译后会把同一个「力量被暂时收住」事实说多次。这里只做
 * 同段语义去重，不增删其他卦盘事实。
 */
function dedupePlainFacts(text) {
  let seenTomb = false;
  return text
    .replace(/(代表爻)?力量被暂时收住(?:，暂时难以发挥)?/gu, (match, subject = '') => {
      if (seenTomb) return '';
      seenTomb = true;
      return `${subject}力量被暂时收住，暂时难以发挥`;
    })
    .replace(/[，、]　*；/gu, '；')
    .replace(/；　*。/gu, '。')
    .replace(/；{2,}/gu, '；')
    .replace(/，{2,}/gu, '，');
}

export function plainKeyPoint(technical) {
  let plain = replaceStageContexts(replaceStateContexts(replaceCalendarContexts(String(technical))));
  // 先处理会与短词重叠的完整结构，避免「日冲墓」被拆成「日冲 + 墓」、
  // 「六合支」被误当成整个卦的六合格局。
  plain = plain
    // 飞神压伏是飞伏关系，不是「忌神克用神」。必须在通用的
    // 「克用神」替换前吃掉整个结构，避免两种关系被翻成同一个「妨碍」。
    .replace(/\u4e3a飞神，克用神([^\uff0c\uff1b\u3002]+)，飞神克伏神/gu,
      `盖在藏起来的代表爻上面，压住$1，使它暂时出不来`)
    .replace(/\u4e3a飞神，生用神([^\uff0c\uff1b\u3002]+)，飞神生伏神/gu,
      `盖在藏起来的代表爻上面，同时帮助$1`)
    .replaceAll('日冲墓', '日用相冲的地支解开收藏状态')
    .replaceAll('冲墓', '用相冲的地支解开收藏状态')
    .replaceAll('六合支', '相合地支')
    .replaceAll('地支六合', '两个地支相合')
    .replaceAll('月破(到底而破)', '被当月之气冲散（持续受冲）')
    .replaceAll('月破(可解)', '被当月之气冲散（另有生助或变化）')
    .replaceAll('旬空(空而不空)', '暂时不在位（已有力量使它仍可起作用）')
    .replaceAll('动化', '变化后成为')
    .replaceAll('值本支实破', '该地支自身值日')
    .replaceAll('生扶', '帮助')
    .replaceAll('比扶', '同五行帮助')
    .replace(/([甲乙丙丁戊己庚辛壬癸][子丑寅卯辰巳午未申酉戌亥])日合该动爻/gu, '$1日与该变化爻相合')
    .replace(/进入([子丑寅卯辰巳午未申酉戌亥])月冲静爻/gu, '进入$1月，以月支冲动该静爻')
    .replace(/进入([子丑寅卯辰巳午未申酉戌亥])年冲静爻/gu, '进入$1年，以年支冲动该静爻')
    .replace(/以([子丑寅卯辰巳午未申酉戌亥])支冲去克神/gu, '以$1支冲开妨碍主角的力量')
    .replace(/逢([子丑寅卯辰巳午未申酉戌亥])支长生/gu, '$1支处在力量开始生发的位置')
    .replaceAll('发动妨碍', '正在变化并妨碍')
    .replaceAll('发动帮助', '正在变化并帮助')
    .replaceAll('发动，', '正在变化，')
    .replaceAll('发动；', '正在变化；')
    .replace(/标记为墓(?=[、；。])/gu, '标记为收藏状态')
    .replace(/([木火土金水])墓于([子丑寅卯辰巳午未申酉戌亥])/gu, '$1在$2被收藏');
  for (const [term, replacement] of REPLACEMENTS) plain = plain.replaceAll(term, replacement);
  for (const [term, replacement] of Object.entries(PLAIN_TERMS)
    .filter(([term]) => term.length > 1)
    .sort(([a], [b]) => b.length - a.length)) {
    plain = plain.replaceAll(term, replacement);
  }
  plain = plain
    .replaceAll('发动妨碍', '正在变化并妨碍')
    .replaceAll('发动帮助', '正在变化并帮助')
    .replaceAll('妨碍主角的力量发动', '妨碍主角的力量正在变化')
    .replaceAll('帮助主角的力量发动', '帮助主角的力量正在变化')
    .replaceAll('所问之事的主角', '代表爻')
    .replaceAll('主角', '代表爻')
    .replaceAll('代表爻：是这一卦中代表爻', '代表爻');
  if (String(technical).includes('本卦六冲')) {
    plain = plain.replace(
      /1爻[^；。]+?构成当前卦整体格局松散、易变/u,
      '第一与第四、第二与第五、第三与第六三组爻两两相冲'
    );
  }
  if (String(technical).includes('本卦六合')) {
    plain = plain.replace(
      /1爻[^；。]+?构成当前卦整体格局紧密、稳定/u,
      '第一与第四、第二与第五、第三与第六三组爻两两相合'
    );
  }
  return dedupePlainFacts(plain);
}

function categoryLabel(result) {
  return CATEGORY_FOCUS_LABELS[result.category] || '所问之事';
}

function fullLine(line) {
  return `${line.position}爻${line.relative}${line.ganzhi}（${line.element}）`;
}

const shortLine = (line) => `${line.relative}${line.ganzhi}`;

function lineRole(result, line) {
  if (!line) return null;
  const sameLine = (candidate) => candidate.position === line.position
    && candidate.relative === line.relative && candidate.ganzhi === line.ganzhi;
  if (result.useGod?.原神爻?.some(sameLine)) return '原神';
  if (result.useGod?.忌神爻?.some(sameLine)) return '忌神';
  if (result.useGod?.仇神爻?.some(sameLine)) return '仇神';
  return null;
}

function roleIntroduction(line, role, primary) {
  if (role === '原神') return `${fullLine(line)}生代表爻之${primary.element}，是帮助的力量`;
  if (role === '忌神') return `${fullLine(line)}克代表爻之${primary.element}，是妨碍的力量`;
  return `${fullLine(line)}生助妨碍代表爻的力量，是助长妨碍的力量`;
}

/** 先交代原神/忌神/仇神的身份和作用方向，再陈述它自身状态。 */
function prependRoleIdentity(technical, plain, result, primary) {
  // 飞神优先按飞伏关系表述，即使该明爻恰好也是忌神，也不混称「妨碍」。
  if (technical.includes('飞神')) return plain;
  const match = String(technical).match(new RegExp(`^([1-6])爻(${RELATIVES})([${STEMS}][${BRANCHES}])`, 'u'));
  if (!match) return plain;
  const line = result.yao[Number(match[1]) - 1];
  const role = lineRole(result, line);
  if (!role) return plain;

  const identity = roleIntroduction(line, role, primary);
  const full = fullLine(line);
  let rest = plain.replace(full, '它').replace(`${line.position}爻${line.relative}${line.ganzhi}`, '它');
  rest = rest
    .replace(new RegExp(`；对代表爻而言是${PLAIN_TERMS[role]}`, 'gu'), '')
    .replace(/；在五行关系上(?:帮助|妨碍)代表爻/gu, '')
    .replace(/；对代表爻而言是助长妨碍的力量/gu, '');
  return `${identity}；${rest}`;
}

function focusText(result, output) {
  const evidence = output.facts.用神;
  const primary = evidence.primary;
  if (!result.category && result.useGod?.用神 === '世爻') {
    return '这一卦以世爻为代表。';
  }
  const label = categoryLabel(result);
  if (evidence.isAbsent) {
    const flying = evidence.flying;
    return `代表${label}的${primary.relative}${primary.ganzhi}（${primary.element}）没有直接出现，`
      + `藏在${flying.position}爻${flying.relative}${flying.ganzhi}下面。`;
  }
  const questionPrefix = result.question ? `对于“${result.question}”，` : '';
  return `${questionPrefix}代表${label}的是${fullLine(primary)}。`;
}

function summaryText(result, output) {
  const use = output.facts.用神;
  const primary = use.primary;
  const label = categoryLabel(result);
  const first = !result.category && result.useGod?.用神 === '世爻'
    ? `这一卦以${fullLine(primary)}的世爻为代表。`
    : use.isAbsent
    ? `这次问的是${label}，${shortLine(primary)}（${primary.element}）藏在${use.flying.position}爻之下，作为代表爻。`
    : `这次问的是${label}，${fullLine(primary)}是代表爻。`;

  const summaryStates = {
    旬空: '暂时不在位', 月破: '被当月之气冲散', 日破: '被当天之气冲散',
    入墓: '力量被暂时收住', 入绝: '力量衰到最低',
    化进神: '变化后更进一步',
    化退神: '变化后退回去', 化绝: '变化到最弱处', 化空: '变化后暂时不在位'
  };
  const specials = output.facts.特殊状态
    .map((state) => state.replace(/^动化.+?\((.+)\)$/u, '$1'))
    // 回头生/回头克由下方的变化句解释一次，避免摘要重复同一事实。
    .filter((state) => ['旬空', '月破', '日破', '入墓', '入绝', '化进神', '化退神', '化绝', '化空'].some((term) => state.includes(term)))
    .slice(0, 2)
    .map((state) => summaryStates[state] || state.replace(/[()]/gu, ''));
  const stateText = STATE_PLAIN[primary.state] || primary.state;
  const second = `从力量看，代表爻在当月${stateText}`
    + `${specials.length ? `，还出现${specials.join('、')}` : ''}。`;

  const generated = output.facts.得生[0]?.from || result.useGod?.原神爻?.[0] || null;
  const overcomeRelation = output.facts.受克[0] || null;
  const overcome = overcomeRelation?.from || result.useGod?.忌神爻?.[0] || null;
  let third;
  if (use.isAbsent && overcomeRelation?.sourceKind === '飞神') {
    third = generated
      ? `${shortLine(overcome)}盖住代表爻，${shortLine(generated)}提供帮助。`
      : `${shortLine(overcome)}盖住代表爻，使它暂时出不来。`;
  } else if (generated && overcome) {
    const weak = ['休', '囚', '死'].includes(generated.state) ? '力量不足但仍' : '';
    third = `${shortLine(generated)}${weak}提供帮助，${shortLine(overcome)}形成妨碍。`;
  } else if (generated) {
    const weak = ['休', '囚', '死'].includes(generated.state) ? '力量不足但仍' : '';
    third = `${shortLine(generated)}${weak}提供帮助，未见直接妨碍。`;
  } else if (overcome) {
    const restrained = overcomeRelation?.是否有救 ? '另有变化爻制约它' : '未见变化爻制约它';
    third = `${shortLine(overcome)}形成妨碍，${restrained}。`;
  } else {
    third = '关系上，未见变化爻直接帮助或妨碍代表爻。';
  }

  const transform = primary.transform;
  const fourth = transform
    ? `变化上，代表爻变为${transform.toRelative}${transform.to}，${PLAIN_TERMS[transform.kind] || transform.kind}。`
    : result.moving.length
      ? `变化上，共有${result.moving.length}爻变化，代表爻本身没有变化。`
      : '变化上，六爻都没有变化。';
  return [first, second, third, fourth, PLAIN_VERDICT_NOTE].join('');
}

const DIGEST_STATE = Object.freeze({
  旺: '目前基础较强。',
  相: '目前基础较稳。',
  休: '目前基础一般。',
  囚: '目前受到限制，推动力偏弱。',
  死: '目前推动力较弱。',
  平: '目前力量普通。'
});

const DIGEST_SPECIALS = Object.freeze([
  ['旬空', '有些条件暂时还没落到实处。'],
  ['月破', '当前条件容易被当月环境冲散。'],
  ['日破', '当前条件容易受当天环境干扰。'],
  ['入墓', '力量暂时被压住，还难充分发挥。'],
  ['入绝', '力量目前很难发挥。'],
  ['化空', '变化后仍有暂时难落实的一面。'],
  ['化绝', '变化后发挥空间进一步减弱。'],
  ['化进神', '变化后有继续增强的迹象。'],
  ['化退神', '变化后有逐渐减弱的迹象。']
]);

const DIGEST_TRANSFORM = Object.freeze({
  回头生: '代表这件事的力量正在变化，变化后反而得到帮助。',
  回头克: '代表这件事的力量正在变化，变化后反而受到压制。',
  化进神: '代表这件事的力量正在变化，变化后有继续增强的迹象。',
  化退神: '代表这件事的力量正在变化，变化后有逐渐减弱的迹象。',
  化绝: '代表这件事的力量正在变化，变化后更难发挥。',
  化空: '代表这件事的力量正在变化，变化后暂时难以落实。',
  化墓: '代表这件事的力量正在变化，变化后暂时被压住。'
});

function digestFocus(result, use) {
  const label = categoryLabel(result);
  if (!result.category && result.useGod?.用神 === '世爻') {
    return '这次主要看问卦的人自身。';
  }
  if (use.isAbsent) {
    return `这次问的是${label}。代表这件事的力量没有直接显现，藏在第${use.flying?.position || use.primary.position}爻下面。`;
  }
  return `这次问的是${label}，卦里用第${use.primary.position}爻代表这件事。`;
}

function digestState(output) {
  const primary = output.facts.用神.primary;
  const base = DIGEST_STATE[primary.state] || '目前的基础力量还需要结合细节查看。';
  const special = DIGEST_SPECIALS.find(([term]) => (
    (output.facts.特殊状态 || []).some((item) => item.includes(term))
  ));
  return `${base}${special ? special[1] : ''}`;
}

function digestRelations(output) {
  const use = output.facts.用神;
  const helps = output.facts.得生 || [];
  const obstacles = output.facts.受克 || [];
  if (use.isAbsent && obstacles[0]?.sourceKind === '飞神') {
    return obstacles[0].是否有救
      ? '有一股力量压着它，使它暂时难以显现；同时也有其他变化力量在牵制这项阻力。'
      : '有一股力量压着它，使它暂时难以显现，目前没看到其他变化力量牵制这项阻力。';
  }
  if (helps.length && obstacles.length) {
    const rescued = obstacles.some(({ 是否有救 }) => 是否有救);
    return '盘里既有正在变化的力量提供帮助，也有正在变化的力量形成阻力。'
      + (rescued ? '其中至少一项阻力也受到其他变化力量牵制。' : '两边都存在，暂时不能只看一面。');
  }
  if (helps.length) {
    const restrained = helps.some(({ 是否受制 }) => 是否受制);
    return restrained
      ? '盘里有正在变化的力量提供帮助，但这股助力本身也受到牵制。'
      : '盘里有正在变化的力量直接提供帮助，暂时没看到直接阻力。';
  }
  if (obstacles.length) {
    const rescued = obstacles.some(({ 是否有救 }) => 是否有救);
    return rescued
      ? '盘里有正在变化的力量形成阻力，同时也有其他变化力量在牵制这项阻力。'
      : '盘里有正在变化的力量直接形成阻力，目前没看到其他变化力量牵制它。';
  }
  return '暂时没看到正在变化的力量直接帮助或压制这件事，结果更依赖现有基础。';
}

function digestChange(result, output) {
  const transform = output.facts.用神.primary.transform;
  if (transform) {
    return DIGEST_TRANSFORM[transform.kind]
      || '代表这件事的力量正在变化，具体变化关系可在“术语”页核对。';
  }
  if (result.moving.length) {
    return `代表这件事的这一爻本身没变，但卦里另有${result.moving.length}处变化，外围条件仍在变化。`;
  }
  return '整卦没有变化，当前结构相对固定。';
}

/**
 * 给普通用户看的短报告。这里只压缩结构化事实，不复述完整 keyPoints。
 * 完整逐条证据仍保留在 chain 和术语页中，供复核使用。
 */
function digestSections(result, output) {
  return [
    { key: 'focus', title: '这次主要看什么', text: digestFocus(result, output.facts.用神) },
    { key: 'state', title: '目前处境', text: digestState(output) },
    { key: 'relations', title: '助力与阻力', text: digestRelations(output) },
    { key: 'change', title: '后续变化', text: digestChange(result, output) }
  ];
}

export function plainVerdict(result) {
  const output = verdict(result);
  const focus = focusText(result, output);
  const chain = output.keyPoints.map((technical) => ({
    technical,
    plain: shortenRepeatedLineRefs(
      prependRoleIdentity(
        technical,
        plainKeyPoint(technical),
        result,
        output.facts.用神.primary
      ),
      output.facts.用神.primary
    )
  }));
  return {
    question: result.question || '',
    focus,
    chain,
    summary: summaryText(result, output),
    sections: digestSections(result, output)
  };
}
